import * as React from "react";

/**
 * Panning and zooming the board (ACL-338).
 *
 * A CSS transform on one world div, not an SVG `viewBox`: the cards are ordinary focusable HTML
 * above an SVG wire layer, and a transform moves both together without touching either's markup.
 *
 * The maths is exported as plain functions so the invariant that matters — the point under the
 * cursor does not move while zooming — can be asserted rather than eyeballed.
 */

export interface Viewport {
  x: number;
  y: number;
  scale: number;
}

export const ZOOM_MIN = 0.35;
export const ZOOM_MAX = 2;

/**
 * Whether a press here should pan the board.
 *
 * Only the empty ground pans. A press on a card, or on any control — the zoom cluster sits inside
 * the stage — belongs to that element: capturing the pointer for a pan steals it and the click
 * never lands, which is what made the fit button and the path menus look dead.
 *
 * **A dropdown's own list is not on the card.** Radix portals a Select's listbox, a popover and a
 * dialog to the end of the body, so `[data-card]` does not contain them and the selector below
 * decides. Without `role="option"` and the popper wrapper in it, picking a firm on the live
 * canvas pressed what looked like empty ground: the stage captured the pointer and the option was
 * never chosen — the same fault as the fit button, one portal further away.
 */
export function startsAPan(target: Element | null): boolean {
  if (!target) return true;
  if (target.closest("[data-card]")) return false;
  return !target.closest(
    "button, a, input, select, textarea, label," +
      " [role='menuitem'], [role='option'], [role='listbox'], [role='dialog']," +
      " [data-radix-popper-content-wrapper], [data-no-pan]",
  );
}

export function clampScale(scale: number): number {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, scale));
}

/** Where a point on the screen falls on the board. */
export function toWorld(view: Viewport, screenX: number, screenY: number) {
  return { x: (screenX - view.x) / view.scale, y: (screenY - view.y) / view.scale };
}

/**
 * Zoom about a fixed point on the screen.
 *
 * The world point under (screenX, screenY) is the same before and after, which is what makes
 * wheel-zoom feel like it is pulling the board rather than sliding it.
 */
export function zoomAt(view: Viewport, screenX: number, screenY: number, factor: number): Viewport {
  const scale = clampScale(view.scale * factor);
  if (scale === view.scale) return view;
  const world = toWorld(view, screenX, screenY);
  return { scale, x: screenX - world.x * scale, y: screenY - world.y * scale };
}

/** The view that fits a box into the stage, never magnifying past 1. */
export function fitTo(
  box: { x: number; y: number; w: number; h: number },
  stageW: number,
  stageH: number,
): Viewport {
  // jsdom, and a stage that has not been laid out yet, both measure zero.
  if (!(stageW > 0) || !(stageH > 0) || !(box.w > 0) || !(box.h > 0)) {
    return { x: 0, y: 0, scale: 1 };
  }
  const scale = clampScale(Math.min(stageW / box.w, stageH / box.h, 1));
  return {
    scale,
    x: (stageW - box.w * scale) / 2 - box.x * scale,
    y: (stageH - box.h * scale) / 2 - box.y * scale,
  };
}

/** Nudge the view so a box is on screen, moving as little as possible. */
export function ensureVisible(
  view: Viewport,
  box: { x: number; y: number; w: number; h: number },
  stageW: number,
  stageH: number,
  margin = 24,
): Viewport {
  if (!(stageW > 0) || !(stageH > 0)) return view;
  const left = box.x * view.scale + view.x;
  const top = box.y * view.scale + view.y;
  const right = left + box.w * view.scale;
  const bottom = top + box.h * view.scale;
  let { x, y } = view;
  if (left < margin) x += margin - left;
  else if (right > stageW - margin) x -= right - (stageW - margin);
  if (top < margin) y += margin - top;
  else if (bottom > stageH - margin) y -= bottom - (stageH - margin);
  return x === view.x && y === view.y ? view : { ...view, x, y };
}

/**
 * The hook the board uses: the view, a stage ref, and the handlers that drive it.
 *
 * Wheel is bound through `addEventListener` with `passive: false` rather than as a JSX prop:
 * React attaches wheel passively, so `preventDefault` on the prop is ignored and the page scrolls
 * behind the board instead of zooming it.
 */
export function useViewport() {
  const stage = React.useRef<HTMLDivElement | null>(null);
  const [view, setView] = React.useState<Viewport>({ x: 0, y: 0, scale: 1 });
  const dragging = React.useRef<{ x: number; y: number; vx: number; vy: number } | null>(null);

  const sizeOf = React.useCallback(() => {
    const box = stage.current?.getBoundingClientRect();
    return { w: box?.width ?? 0, h: box?.height ?? 0 };
  }, []);

  React.useEffect(() => {
    const node = stage.current;
    if (!node) return;
    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey) {
        // Plain wheel pans, which is what a trackpad two-finger scroll should do on a board.
        event.preventDefault();
        const step = event.deltaMode === 1 ? 16 : 1;
        setView((was) => ({ ...was, x: was.x - event.deltaX * step, y: was.y - event.deltaY * step }));
        return;
      }
      event.preventDefault();
      const box = node.getBoundingClientRect();
      // Firefox reports wheel deltas in lines, not pixels, and would otherwise zoom ~16x slower.
      const delta = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
      const factor = Math.exp(-delta / 400);
      setView((was) => zoomAt(was, event.clientX - box.left, event.clientY - box.top, factor));
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    return () => node.removeEventListener("wheel", onWheel);
  }, []);

  const onPointerDown = React.useCallback((event: React.PointerEvent) => {
    if (event.button !== 0 || !startsAPan(event.target as HTMLElement)) return;
    dragging.current = { x: event.clientX, y: event.clientY, vx: 0, vy: 0 };
    setView((was) => {
      dragging.current = { x: event.clientX, y: event.clientY, vx: was.x, vy: was.y };
      return was;
    });
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }, []);

  const onPointerMove = React.useCallback((event: React.PointerEvent) => {
    const from = dragging.current;
    if (!from) return;
    setView((was) => ({
      ...was,
      x: from.vx + (event.clientX - from.x),
      y: from.vy + (event.clientY - from.y),
    }));
  }, []);

  const onPointerUp = React.useCallback((event: React.PointerEvent) => {
    dragging.current = null;
    const node = event.currentTarget as HTMLElement;
    if (node.hasPointerCapture?.(event.pointerId)) node.releasePointerCapture(event.pointerId);
  }, []);

  /**
   * Fit a box to the stage. Returns false when the stage has not been laid out yet, so the
   * caller knows this was a no-op and can try again once it has a size.
   */
  const fit = React.useCallback(
    (box: { x: number; y: number; w: number; h: number }) => {
      const { w, h } = sizeOf();
      if (!(w > 0) || !(h > 0)) return false;
      setView(fitTo(box, w, h));
      return true;
    },
    [sizeOf],
  );

  const zoomBy = React.useCallback(
    (factor: number) => {
      const { w, h } = sizeOf();
      setView((was) => zoomAt(was, w / 2, h / 2, factor));
    },
    [sizeOf],
  );

  const reveal = React.useCallback(
    (box: { x: number; y: number; w: number; h: number }) => {
      const { w, h } = sizeOf();
      setView((was) => ensureVisible(was, box, w, h));
    },
    [sizeOf],
  );

  return {
    stage,
    view,
    panning: dragging.current !== null,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    fit,
    zoomBy,
    reveal,
  };
}
